import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DevicePhotos = ({ photos = [], onChange, maxPhotos = 5, authHeader, onPhotoClick, folder = 'repairs', onBusyChange,
  subjectLabel = 'equipo', emptyHint = 'Documenta el estado del dispositivo al recibirlo' }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    processFiles(files);
  };

  const processFiles = async (files) => {
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Máximo ${maxPhotos} fotos permitidas`);
      return;
    }

    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/');
      const isUnder10MB = file.size <= 10 * 1024 * 1024;
      
      if (!isValid) {
        toast.error(`${file.name} no es una imagen válida`);
        return false;
      }
      if (!isUnder10MB) {
        toast.error(`${file.name} es muy grande (máx 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    onBusyChange?.(true);
    toast.info(`Subiendo ${validFiles.length} foto(s)...`);

    try {
      const uploadedUrls = await Promise.all(
        validFiles.map(file => uploadToCloudinary(file))
      );

      await onChange([...photos, ...uploadedUrls]);
      toast.success(`${validFiles.length} foto(s) subida(s) exitosamente`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar las fotos. Intenta nuevamente.');
    } finally {
      setUploading(false);
      onBusyChange?.(false);
    }
  };

  const uploadToCloudinary = async (file) => {
    // Get signature from backend
    const sigResponse = await axios.get(`${API}/api/cloudinary/signature`, {
      headers: authHeader,
      params: {
        resource_type: 'image',
        folder
      }
    });

    const { signature, timestamp, cloud_name, api_key, folder: signedFolder } = sigResponse.data;

    // Upload to Cloudinary
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', api_key);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('folder', signedFolder);

    const uploadResponse = await fetch(
      `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!uploadResponse.ok) {
      throw new Error('Cloudinary upload failed');
    }

    const result = await uploadResponse.json();
    return result.secure_url; // Return the URL
  };

  const removePhoto = async (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setUploading(true);
    onBusyChange?.(true);
    try {
      await onChange(newPhotos);
      toast.success('Foto eliminada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo quitar la foto');
    } finally {
      setUploading(false);
      onBusyChange?.(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="device-photos-component">
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={photos.length >= maxPhotos || uploading}
          className="flex-1"
          data-testid="upload-photo-button"
        >
          <Upload size={18} className="mr-2" />
          {uploading ? 'Subiendo...' : 'Subir Fotos'}
        </Button>
        
        <Button
          type="button"
          variant="outline"
          onClick={() => cameraInputRef.current?.click()}
          disabled={photos.length >= maxPhotos || uploading}
          className="flex-1"
          data-testid="take-photo-button"
        >
          <Camera size={18} className="mr-2" />
          Tomar Foto
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileUpload}
        className="hidden"
        disabled={uploading}
      />
      
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileUpload}
        className="hidden"
        disabled={uploading}
      />

      {photos.length > 0 ? (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
              {photos.length} de {maxPhotos} fotos
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((photoUrl, index) => (
              <div
                key={index}
                className="relative group rounded-lg overflow-hidden border-2 border-zinc-200 hover:border-blue-400 transition-colors"
                data-testid={`photo-preview-${index}`}
              >
                {onPhotoClick ? (
                  <button type="button" onClick={() => onPhotoClick(photoUrl)} className="block w-full">
                    <img
                      src={photoUrl}
                      alt={`Foto del ${subjectLabel} ${index + 1}`}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                  </button>
                ) : (
                  <img
                    src={photoUrl}
                    alt={`Foto del ${subjectLabel} ${index + 1}`}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute top-1 right-1 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                  data-testid={`remove-photo-${index}`}
                  disabled={uploading}
                >
                  <X size={14} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 px-2">
                  Foto {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center">
          <ImageIcon size={40} className="mx-auto text-zinc-400 mb-3" />
          <p className="text-sm text-zinc-600 mb-1">
            No hay fotos del {subjectLabel}
          </p>
          <p className="text-xs text-zinc-500">
            {emptyHint}
          </p>
        </div>
      )}

    </div>
  );
};

export default DevicePhotos;
